import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCheck } from '@fortawesome/free-solid-svg-icons';

// ========== STATIC LANGUAGE MAP (for custom search) ==========
export const LANGUAGE_MAP = {
  en: 'English',
  ru: 'Russian',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
  hi: 'Hindi',
  pt: 'Portuguese',
  ar: 'Arabic',
  tr: 'Turkish',
  th: 'Thai',
  nl: 'Dutch',
  sv: 'Swedish',
  pl: 'Polish',
  da: 'Danish',
  no: 'Norwegian',
  fi: 'Finnish',
  el: 'Greek',
  cs: 'Czech',
  hu: 'Hungarian',
  he: 'Hebrew',
  id: 'Indonesian',
  ms: 'Malay',
  vi: 'Vietnamese',
  tl: 'Filipino',
  fa: 'Persian',
  bn: 'Bengali',
  ta: 'Tamil',
  te: 'Telugu',
  mr: 'Marathi',
  kn: 'Kannada',
  ml: 'Malayalam',
  ur: 'Urdu',
  uk: 'Ukrainian',
  ro: 'Romanian',
  sk: 'Slovak',
  bg: 'Bulgarian',
  hr: 'Croatian',
  sl: 'Slovenian',
  et: 'Estonian',
  lv: 'Latvian',
  lt: 'Lithuanian',
  ka: 'Georgian',
  hy: 'Armenian',
  sq: 'Albanian',
  mk: 'Macedonian',
  sr: 'Serbian',
};

// Reverse map for searching by name
const LANGUAGE_NAME_TO_CODE = Object.fromEntries(
  Object.entries(LANGUAGE_MAP).map(([code, name]) => [name.toLowerCase(), code])
);

const Filters = ({
  type = 'movies',
  onGenreChange,
  onLanguageChange,
  onYearChange,
  onSortChange,
  onProviderChange,
  onSearch,
  genreValue,
  languageValue,
  yearValue,
  sortValue,
  providerValue,
  lang,
  translations
}) => {
  // ----- Local state for temporary filter values -----
  const [tempFilters, setTempFilters] = useState({
    genre: genreValue,
    language: languageValue,
    year: yearValue,
    sort: sortValue,
    provider: providerValue || ''
  });

  // ----- Custom language state -----
  const [customMode, setCustomMode] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [customSuggestion, setCustomSuggestion] = useState(null); // { code, name }
  const [confirmedLanguage, setConfirmedLanguage] = useState(null); // { code, name } or null

  const inputRef = useRef(null);

  // ----- Translation helpers (with fallbacks for new keys) -----
  const t = (key) => {
    const langData = translations[lang] || translations.en;
    if (key === 'customLanguage') return langData.customLanguage || 'Custom';
    if (key === 'languagePlaceholder') return langData.languagePlaceholder || 'Type language...';
    return langData[key] || key;
  };

  const tNested = (objKey, key) => {
    const langData = translations[lang] || translations.en;
    const obj = langData[objKey];
    return obj ? obj[key] : key;
  };

  // ----- Sync temp filters with props -----
  useEffect(() => {
    setTempFilters({
      genre: genreValue,
      language: languageValue,
      year: yearValue,
      sort: sortValue,
      provider: providerValue || ''
    });
  }, [genreValue, languageValue, yearValue, sortValue, providerValue]);

  // ----- Handlers for filter changes (temp) -----
  const handleFilterChange = (filterType, value) => {
    setTempFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // ----- Custom language handlers -----
  const handleCustomToggle = () => {
    setCustomMode(!customMode);
    if (!customMode) {
      // entering custom mode: focus input
      setTimeout(() => inputRef.current?.focus(), 50);
      if (confirmedLanguage) {
        setCustomInput(confirmedLanguage.name);
        setCustomSuggestion(null);
      } else {
        setCustomInput('');
        setCustomSuggestion(null);
      }
    } else {
      // exiting custom mode without confirming: revert to dropdown value
      setCustomInput('');
      setCustomSuggestion(null);
      if (confirmedLanguage) {
        setTempFilters(prev => ({ ...prev, language: languageValue }));
      }
      setConfirmedLanguage(null);
    }
  };

  const handleCustomInputChange = (e) => {
    const value = e.target.value;
    setCustomInput(value);
    const lower = value.toLowerCase().trim();
    if (lower === '') {
      setCustomSuggestion(null);
      return;
    }
    let match = null;
    const exactCode = LANGUAGE_NAME_TO_CODE[lower];
    if (exactCode) {
      match = { code: exactCode, name: LANGUAGE_MAP[exactCode] };
    } else {
      const matchedName = Object.keys(LANGUAGE_NAME_TO_CODE).find(name =>
        name.startsWith(lower)
      );
      if (matchedName) {
        const code = LANGUAGE_NAME_TO_CODE[matchedName];
        match = { code, name: LANGUAGE_MAP[code] };
      }
    }
    setCustomSuggestion(match);
  };

  const handleConfirmCustomLanguage = () => {
    if (customSuggestion) {
      const { code, name } = customSuggestion;
      setConfirmedLanguage({ code, name });
      setTempFilters(prev => ({ ...prev, language: code }));
      setCustomInput(name);
      setCustomSuggestion(null);
      // Do NOT call onLanguageChange or onSearch here – wait for "Apply Filters"
    } else {
      setConfirmedLanguage(null);
      setTempFilters(prev => ({ ...prev, language: '' }));
      setCustomInput('');
      setCustomSuggestion(null);
    }
  };

  const handleCustomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirmCustomLanguage();
    } else if (e.key === 'Escape') {
      handleCustomToggle();
    }
  };

  // ----- Handle language select from dropdown (normal mode) -----
  const handleLanguageSelect = (e) => {
    const value = e.target.value;
    if (customMode) {
      setCustomMode(false);
      setCustomInput('');
      setCustomSuggestion(null);
      setConfirmedLanguage(null);
    }
    setTempFilters(prev => ({ ...prev, language: value }));
    // Do not call onLanguageChange yet; wait for search.
  };

  // ----- Apply filters (Search button) -----
  const handleSearchClick = () => {
    // If in custom mode and not confirmed, reset language to empty
    if (customMode && !confirmedLanguage) {
      setTempFilters(prev => ({ ...prev, language: '' }));
      setConfirmedLanguage(null);
    }
    // Now apply all filters
    onGenreChange(tempFilters.genre);
    onLanguageChange(tempFilters.language);
    onYearChange(tempFilters.year);
    onSortChange(tempFilters.sort);
    if (onProviderChange) onProviderChange(tempFilters.provider);
    onSearch();
  };

  // ----- Helper to build options -----
  const sortOptionsWithEmptyFirst = (obj) => {
    const keys = Object.keys(obj);
    keys.sort((a, b) => {
      if (a === '') return -1;
      if (b === '') return 1;
      return 0;
    });
    return keys.map(key => ({
      value: key,
      text: obj[key] || key
    }));
  };

  // ----- Filter configs (excluding language) -----
  const genreObj = type === 'movies' ? 'movieGenres' : 'seriesGenres';
  const sortObj = type === 'movies' ? 'movieSortOptions' : 'seriesSortOptions';
  const yearLabel = type === 'movies' ? 'releaseYear' : 'firstAirYear';

  const otherFilters = [
    {
      id: type === 'movies' ? 'genreSelect' : 'seriesGenreSelect',
      label: t('genre'),
      value: tempFilters.genre,
      filterType: 'genre',
      options: sortOptionsWithEmptyFirst(translations[lang][genreObj] || {}),
    },
    {
      id: type === 'movies' ? 'yearSelect' : 'seriesYearSelect',
      label: t(yearLabel),
      value: tempFilters.year,
      filterType: 'year',
      options: sortOptionsWithEmptyFirst(translations[lang].years || {}),
    },
    {
      id: type === 'movies' ? 'sortSelect' : 'seriesSortSelect',
      label: t('sortBy'),
      value: tempFilters.sort,
      filterType: 'sort',
      options: sortOptionsWithEmptyFirst(translations[lang][sortObj] || {}),
    },
    {
      id: type === 'movies' ? 'providerSelect' : 'seriesProviderSelect',
      label: t('provider'),
      value: tempFilters.provider,
      filterType: 'provider',
      options: sortOptionsWithEmptyFirst(translations[lang].providers || {}),
    },
  ];

  return (
    <div className="filters-container">
      {/* Genre */}
      <div className="filter-group">
        <label className="filter-label" htmlFor={otherFilters[0].id}>
          {otherFilters[0].label}
        </label>
        <select
          id={otherFilters[0].id}
          className="filter-select"
          value={otherFilters[0].value}
          onChange={(e) => handleFilterChange(otherFilters[0].filterType, e.target.value)}
        >
          {otherFilters[0].options.map(option => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      </div>

      {/* Language with custom button */}
      <div className="filter-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label className="filter-label" htmlFor="languageSelect">
            {t('language')}
          </label>
          <button
            onClick={handleCustomToggle}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              color: customMode ? '#59f001' : 'rgba(255,255,255,0.7)',
              padding: '0 10px',
              borderRadius: '12px',
              fontSize: '0.7rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              fontWeight: '500',
              lineHeight: '1.6',
              height: '22px',
              whiteSpace: 'nowrap',
            }}
          >
            {customMode ? '×' : t('customLanguage')}
          </button>
        </div>

        {customMode ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                ref={inputRef}
                type="text"
                value={customInput}
                onChange={handleCustomInputChange}
                onKeyDown={handleCustomKeyDown}
                placeholder={t('languagePlaceholder')}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  position: 'relative',
                  zIndex: 2,
                  backgroundColor: 'transparent',
                }}
              />
              {/* Ghost text overlay */}
              {customSuggestion && customInput.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    padding: '8px 12px',
                    fontSize: '0.9rem',
                    color: 'rgba(255,255,255,0.3)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    whiteSpace: 'pre',
                    fontFamily: 'inherit',
                    lineHeight: '1.5',
                    overflow: 'hidden',
                    width: '100%',
                    boxSizing: 'border-box',
                    textAlign: 'left',
                  }}
                >
                  {customInput}
                  <span style={{ color: 'rgba(89, 240, 1, 0.5)' }}>
                    {customSuggestion.name.slice(customInput.length)}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={handleConfirmCustomLanguage}
              style={{
                background: 'rgba(89, 240, 1, 0.15)',
                border: '1px solid #59f001',
                color: '#59f001',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap',
              }}
            >
              <FontAwesomeIcon icon={faCheck} />
            </button>
          </div>
        ) : (
          <select
            id="languageSelect"
            className="filter-select"
            value={tempFilters.language}
            onChange={handleLanguageSelect}
          >
            {sortOptionsWithEmptyFirst(translations[lang].languages || {}).map(option => (
              <option key={option.value} value={option.value}>
                {option.text}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Year */}
      <div className="filter-group">
        <label className="filter-label" htmlFor={otherFilters[1].id}>
          {otherFilters[1].label}
        </label>
        <select
          id={otherFilters[1].id}
          className="filter-select"
          value={otherFilters[1].value}
          onChange={(e) => handleFilterChange(otherFilters[1].filterType, e.target.value)}
        >
          {otherFilters[1].options.map(option => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      </div>

      {/* Sort */}
      <div className="filter-group">
        <label className="filter-label" htmlFor={otherFilters[2].id}>
          {otherFilters[2].label}
        </label>
        <select
          id={otherFilters[2].id}
          className="filter-select"
          value={otherFilters[2].value}
          onChange={(e) => handleFilterChange(otherFilters[2].filterType, e.target.value)}
        >
          {otherFilters[2].options.map(option => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      </div>

      {/* Provider */}
      <div className="filter-group">
        <label className="filter-label" htmlFor={otherFilters[3].id}>
          {otherFilters[3].label}
        </label>
        <select
          id={otherFilters[3].id}
          className="filter-select"
          value={otherFilters[3].value}
          onChange={(e) => handleFilterChange(otherFilters[3].filterType, e.target.value)}
        >
          {otherFilters[3].options.map(option => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      </div>

      {/* Search button */}
      <div className="search-btn-container">
        <button className="search-movies-btn" onClick={handleSearchClick}>
          <FontAwesomeIcon icon={faSearch} />
          {type === 'movies' ? t('searchMovies') : t('searchSeries')}
        </button>
      </div>
    </div>
  );
};

export default Filters;